import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  ReactFlow, Background, Controls, Panel, ReactFlowProvider,
  useNodesState, useEdgesState, useReactFlow,
  Handle, Position, MarkerType,
  type Node, type Edge, type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '@/features/graph/DepGraph.css'
import s from '@/features/graph/DepGraph.module.css'
import dagre from '@dagrejs/dagre'
import {
  forceSimulation, forceLink, forceManyBody, forceCollide, forceX, forceY,
  type SimulationNodeDatum,
} from 'd3-force'
import type { GraphData, GraphEdge, GraphKind, GraphNode } from '@/lib/apiTypes'
import { bundleEdges, type BlastRadius, type EdgeBundle } from '@/features/graph/lib/graphView'
import { GraphEdgeHoverCard } from '@/features/graph/components/GraphEdgeDetail'
import { EmptyState } from '@/shared/components/EmptyState'
import { GraphContextMenu } from '@/features/graph/components/GraphContextMenu'
import { DeprecateModal, UndeprecateModal, BlockModal } from '@/shared/components/ModuleModals'
import { ChipIcon, DatabaseIcon, BoxIcon } from '@/shared/components/Icons'

export type LayoutMode = 'layered' | 'organic'

/**
 * Edge-hover timing.
 *
 * OPEN is a rest, not a crossing: the cursor passes over edges on its way to the one being asked
 * about, and anything much shorter turns a sweep across the canvas into a flicker of cards.
 *
 * CLOSE is a bridge: the card sits beside the cursor, not under it, so leaving the edge for the
 * card necessarily leaves the edge first. The grace period is the same 120ms Tooltip's interactive
 * mode uses, for the same reason - it is the gap the pointer has to cross.
 */
const EDGE_HOVER_DELAY_MS = 140
const EDGE_CLOSE_GRACE_MS = 120

// ── Geometry ──────────────────────────────────────────────────────────────────
//
// Shape carries KIND, colour carries STATUS. Those are the two channels, and conflating them
// was the central flaw of the two graphs this replaces: one used --accent for "policy failed"
// and the other for "workspace", so the same colour meant different things on two pages.
//
// Sizes differ per kind as a third, redundant cue - a provider is a leaf-ish dependency and
// reads fine smaller, a module carries the most text.

interface KindGeometry { w: number; h: number }

const GEOMETRY: Record<GraphKind, KindGeometry> = {
  state: { w: 200, h: 64 },
  module: { w: 220, h: 72 },
  provider: { w: 196, h: 56 },
}

/** Vertical room the cylinder's elliptical lid needs, so text clears it. */
const CYLINDER_RY = 7
/** How far a chip's pins stick out, so they are not clipped by the node box. */
const CHIP_PIN = 3

export const GRAPH_LARGE_THRESHOLD = 150

/**
 * Blast radius drives a subtle size multiplier. Without it a hub that forty things depend on
 * looks exactly like a leaf, which wastes the most informative channel the graph has. Capped
 * low so the layout stays predictable and labels keep their box.
 */
function scaleFor(radius: number | undefined): number {
  if (!radius || radius <= 0) return 1
  if (radius >= 20) return 1.3
  return 1 + (radius / 20) * 0.3
}

function fitMaxZoom(n: number): number {
  if (n <= 8) return 1.6
  if (n >= 40) return 1.0
  return 1.6 - ((n - 8) / 32) * 0.6
}

/**
 * Per-side fit padding, in pixels where an overlay has to be cleared.
 *
 * The legend, the status bar and the hint FLOAT over the canvas, so a symmetric percentage fit
 * tucks real nodes underneath them - which is how two provider nodes ended up hiding behind
 * the legend. Reserving their actual footprint is the fix; the sides without an overlay keep a
 * proportional margin that tightens as the graph grows.
 */
type Pad = `${number}px` | `${number}%`

function fitPadding(n: number): { top: Pad; right: Pad; bottom: Pad; left: Pad } {
  const side: Pad = n <= 10
    ? '14%'
    : n >= GRAPH_LARGE_THRESHOLD
      ? '5%'
      : `${Math.round(14 - ((n - 10) / (GRAPH_LARGE_THRESHOLD - 10)) * 9)}%`
  return {
    top: '72px',     // status bar
    right: '200px',  // legend
    bottom: '46px',  // hint line + zoom controls
    left: side,
  }
}

// ── Status colour ─────────────────────────────────────────────────────────────

/**
 * One scale for every kind. Order is severity, not source: a blocked artefact is the loudest
 * thing on the canvas regardless of why it was blocked.
 */
function statusColor(n: GraphNode): string {
  if (n.blocked) return 'var(--red)'
  if (n.critical_count > 0) return 'var(--red)'
  if (n.deprecated || n.outdated) return 'var(--amber)'
  if (n.high_count > 0) return 'var(--amber)'
  if (n.policy_failed) return 'var(--accent)'
  if (n.external) return 'var(--border)'
  return 'var(--green)'
}

/** A one-line reason, for the node's title attribute. Beats hovering and guessing. */
function statusSummary(n: GraphNode): string {
  const bits: string[] = []
  if (n.blocked) bits.push(n.blocked_reason ? `blocked: ${n.blocked_reason}` : 'blocked')
  if (n.deprecated) bits.push(n.deprecated_message ? `deprecated: ${n.deprecated_message}` : 'deprecated')
  if (n.outdated) bits.push('a workspace pins an older version than its constraint allows')
  if (n.critical_count > 0) bits.push(`${n.critical_count} critical`)
  if (n.high_count > 0) bits.push(`${n.high_count} high`)
  if (n.policy_failed) bits.push('fails a policy')
  if (n.kind === 'state' && n.has_lock_file === false) bits.push('no lock file - provider pins are invisible')
  if (n.external) bits.push('outside this org')
  return bits.length > 0 ? bits.join(' · ') : 'no findings'
}

// ── Silhouettes ───────────────────────────────────────────────────────────────
//
// Drawn as inline SVG behind the label rather than with clip-path: clipping destroys the
// border and the focus ring, which are doing real work here (the border IS the status colour).

function silhouette(kind: GraphKind, w: number, h: number): React.ReactNode {
  if (kind === 'module') {
    // Hexagon. Distinct at a glance from both other outlines, and the conventional shape for
    // a package in infra tooling.
    const cut = Math.min(14, w * 0.08)
    return <path d={`M${cut} 0 L${w - cut} 0 L${w} ${h / 2} L${w - cut} ${h} L${cut} ${h} L0 ${h / 2} Z`} />
  }
  if (kind === 'state') {
    // Cylinder: a datastore. The inner arc is the lid, which is what makes it read as 3D.
    const ry = CYLINDER_RY
    return (
      <>
        <path d={`M0 ${ry} A ${w / 2} ${ry} 0 0 1 ${w} ${ry} L${w} ${h - ry} A ${w / 2} ${ry} 0 0 1 0 ${h - ry} Z`} />
        <path d={`M0 ${ry} A ${w / 2} ${ry} 0 0 0 ${w} ${ry}`} fill="none" />
      </>
    )
  }
  // Chip: body plus pin stubs. A provider is a compiled binary plugin, and pins are what make
  // the outline unmistakable even when the glyph is too small to read.
  const r = 6
  const px = CHIP_PIN
  const bodyW = w - px * 2
  const pins: React.ReactNode[] = []
  for (const frac of [0.3, 0.7]) {
    const y = h * frac
    pins.push(<path key={`l${frac}`} d={`M${px} ${y} L0 ${y}`} fill="none" />)
    pins.push(<path key={`r${frac}`} d={`M${w - px} ${y} L${w} ${y}`} fill="none" />)
  }
  return (
    <>
      <path d={`M${px + r} 0 L${px + bodyW - r} 0 A${r} ${r} 0 0 1 ${px + bodyW} ${r} L${px + bodyW} ${h - r} A${r} ${r} 0 0 1 ${px + bodyW - r} ${h} L${px + r} ${h} A${r} ${r} 0 0 1 ${px} ${h - r} L${px} ${r} A${r} ${r} 0 0 1 ${px + r} 0 Z`} />
      {pins}
    </>
  )
}

/** Where the label can sit without colliding with the silhouette's edges. */
function labelInset(kind: GraphKind): React.CSSProperties {
  if (kind === 'module') return { padding: '8px 18px' }
  if (kind === 'state') return { padding: `${CYLINDER_RY + 5}px 12px ${CYLINDER_RY + 3}px` }
  return { padding: '7px 12px' }
}

const KIND_GLYPH: Record<GraphKind, (size: number) => React.ReactNode> = {
  state: (size) => <DatabaseIcon size={size} />,
  module: (size) => <BoxIcon size={size} />,
  provider: (size) => <ChipIcon size={size} />,
}

const KIND_LABEL: Record<GraphKind, string> = {
  state: 'state file',
  module: 'module',
  provider: 'provider',
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DepGraphProps {
  /** Already narrowed by the page (see lib/graphView computeView). */
  data: GraphData
  loading: boolean
  error: string | null
  height?: number | string
  currentOrg?: string
  layoutMode?: LayoutMode
  highlightIds?: Set<string>
  orphanIds?: Set<string>
  cycleIds?: Set<string>
  blastRadius?: Map<string, BlastRadius>
  /** Controlled, unlike the old one-way prop that silently ignored a non-null value. */
  focusNodeId?: string | null
  /** Click: the page isolates this node's lineage AND opens the detail panel. */
  onNodeActivate?: (node: GraphNode) => void
  onHideNode?: (id: string) => void
  /** A state node with no lock file - the page opens the upload helper. */
  onLockFileHelp?: (node: GraphNode) => void
  /** Something was deprecated/blocked; the page refetches. */
  onMutated?: () => void
  navigable?: boolean
}

// ── Layout ────────────────────────────────────────────────────────────────────

interface Positioned { id: string; x: number; y: number }

/**
 * dagre left-to-right on the connected component, with disconnected nodes corralled into a
 * grid beneath it.
 *
 * The orphan grid is not cosmetic. In production most state workspaces have no lock file, so
 * they have no edges at all - the previous implementation scattered unreachable nodes down a
 * diagonal at y = 300 + i*80, which is unreadable past a handful. A labelled block below the
 * graph keeps them findable (they are a worklist, not noise) without wrecking the real layout.
 */
function layoutNodes(
  nodes: GraphNode[],
  edges: GraphEdge[],
  sizeOf: (n: GraphNode) => KindGeometry,
): Map<string, Positioned> {
  const connectedIds = new Set<string>()
  for (const e of edges) { connectedIds.add(e.from); connectedIds.add(e.to) }

  const connected = nodes.filter((n) => connectedIds.has(n.id))
  const orphans = nodes.filter((n) => !connectedIds.has(n.id))

  const out = new Map<string, Positioned>()
  let maxY = 0
  let minX = 0

  if (connected.length > 0) {
    const g = new dagre.graphlib.Graph()
    g.setGraph({ rankdir: 'LR', nodesep: 56, ranksep: 120 })
    g.setDefaultEdgeLabel(() => ({}))
    for (const n of connected) {
      const { w, h } = sizeOf(n)
      g.setNode(n.id, { width: w, height: h })
    }
    for (const e of edges) {
      if (connectedIds.has(e.from) && connectedIds.has(e.to)) g.setEdge(e.from, e.to)
    }
    dagre.layout(g)
    let first = true
    for (const n of connected) {
      const pos = g.node(n.id)
      const x = pos?.x ?? 0
      const y = pos?.y ?? 0
      out.set(n.id, { id: n.id, x, y })
      maxY = Math.max(maxY, y + (pos?.height ?? 0) / 2)
      minX = first ? x : Math.min(minX, x)
      first = false
    }
  }

  if (orphans.length > 0) {
    const cols = Math.max(1, Math.min(6, Math.ceil(Math.sqrt(orphans.length * 1.6))))
    const colW = 240
    const rowH = 96
    const top = connected.length > 0 ? maxY + 140 : 0
    const left = connected.length > 0 ? minX : 0
    orphans.forEach((n, i) => {
      out.set(n.id, {
        id: n.id,
        x: left + (i % cols) * colW,
        y: top + Math.floor(i / cols) * rowH,
      })
    })
  }

  return out
}

interface SimNode extends SimulationNodeDatum {
  id: string
  x: number
  y: number
  fx: number | null
  fy: number | null
}

// ── Inner component ───────────────────────────────────────────────────────────

function DepGraphInner({
  data,
  loading,
  error,
  height = 420,
  currentOrg,
  layoutMode = 'layered',
  highlightIds,
  orphanIds,
  cycleIds,
  blastRadius,
  focusNodeId = null,
  onNodeActivate,
  onHideNode,
  onLockFileHelp,
  onMutated,
  navigable = true,
}: DepGraphProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const hoveredRef = useRef<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: GraphNode } | null>(null)
  const [deprecateTarget, setDeprecateTarget] = useState<GraphNode | null>(null)
  const [undeprecateTarget, setUndeprecateTarget] = useState<GraphNode | null>(null)
  const [blockTarget, setBlockTarget] = useState<GraphNode | null>(null)
  // Edge inspection is local, not URL state: it is "what is this line", not a view anyone would
  // deep-link, and the Graph page's rule is that nothing is mirrored out of the URL.
  //
  // `at` is where the pointer was when it opened, because the card is placed beside the cursor
  // rather than beside the edge: an edge is a long thin path whose bounding box is mostly empty,
  // so anchoring to it would park the card an arbitrary distance from what was being pointed at.
  const [edgeTarget, setEdgeTarget] = useState<
    { bundle: EdgeBundle; at: { x: number; y: number }; pinned: boolean } | null
  >(null)
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null)
  // The grace period that makes the card hoverable: leaving the edge schedules the close, and
  // reaching the card (or another edge) cancels it. Without it the links inside are unreachable,
  // because getting to them means leaving the edge.
  const edgeCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const edgeOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const simRef = useRef<ReturnType<typeof forceSimulation<any>> | null>(null)
  const draggingRef = useRef<string | null>(null)
  const simNodeIndex = useRef<Map<string, SimNode>>(new Map())
  const containerRef = useRef<HTMLDivElement>(null)
  const { fitView } = useReactFlow()

  const sizeOf = useCallback((n: GraphNode): KindGeometry => {
    const base = GEOMETRY[n.kind]
    const k = scaleFor(blastRadius?.get(n.id)?.total)
    return { w: Math.round(base.w * k), h: Math.round(base.h * k) }
  }, [blastRadius])

  /**
   * Re-layout is keyed on the node/edge SET, not on object identity.
   *
   * useAsync replaces its data object on every refetch, so a deprecate used to re-seed the
   * layout and re-fit the viewport - throwing away your pan, your zoom, and (now) the lineage
   * you had isolated. When the topology is unchanged we only refresh node data.
   */
  const topologySignature = useMemo(
    () => `${layoutMode}|${data.nodes.map((n) => n.id).sort().join(',')}|${data.edges.map((e) => `${e.from}>${e.to}`).sort().join(',')}`,
    [data.nodes, data.edges, layoutMode],
  )

  const fitWhenVisible = useCallback((opts?: Parameters<typeof fitView>[0]) => {
    const el = containerRef.current
    if (!el) return
    if (el.offsetWidth > 0 && el.offsetHeight > 0) { fitView(opts); return }
    const ro = new ResizeObserver(() => {
      if (el.offsetWidth > 0 && el.offsetHeight > 0) { ro.disconnect(); fitView(opts) }
    })
    ro.observe(el)
    setTimeout(() => ro.disconnect(), 2000)
  }, [fitView])

  const nodeDataFor = useCallback((n: GraphNode): DepNodeData => {
    const hovered = hoveredRef.current
    let hoverState: 'none' | 'on' | 'off' = 'none'
    if (hovered) {
      const touching = hovered === n.id
        || data.edges.some((e) => (e.from === hovered && e.to === n.id) || (e.to === hovered && e.from === n.id))
      hoverState = touching ? 'on' : 'off'
    }
    return {
      node: n,
      size: sizeOf(n),
      highlighted: highlightIds ? highlightIds.has(n.id) : false,
      searchDimmed: highlightIds ? !highlightIds.has(n.id) : false,
      isFocus: n.id === focusNodeId,
      isOrphan: orphanIds?.has(n.id) ?? false,
      inCycle: cycleIds?.has(n.id) ?? false,
      blast: blastRadius?.get(n.id),
      hoverState,
      currentOrg,
    }
  }, [data.edges, sizeOf, highlightIds, focusNodeId, orphanIds, cycleIds, blastRadius, currentOrg])

  /**
   * Parallel relationships collapse to one line. Two lock rows for one provider (both
   * registries) or two module calls to one source previously produced duplicate React keys
   * here, because the id was already `from->to:rel`; bundling makes that id unique by
   * construction and gives the detail modal both rows to show.
   */
  const bundles = useMemo(() => bundleEdges(data.edges), [data.edges])

  /** Node ids are addresses, not text: `ws:<uuid>` would be announced as a bare uuid. */
  const labelOf = useCallback(
    (id: string) => data.nodes.find((n) => n.id === id)?.label ?? id,
    [data.nodes],
  )

  const buildEdges = useCallback((): Edge[] => {
    const hovered = hoveredRef.current
    return bundles.map((b) => {
      const nodeActive = hovered !== null && (b.from === hovered || b.to === hovered)
      const edgeActive = hoveredEdgeId === b.key
      const active = nodeActive || edgeActive
      const first = b.edges[0]!
      return {
        id: b.key,
        source: b.from,
        target: b.to,
        animated: false,
        // Only when a line stands for more than one relationship - the common case must look
        // exactly as it did, or every edge gains a label for no information.
        ...(b.edges.length > 1 ? { label: String(b.edges.length) } : {}),
        // The visible stroke is sub-pixel at minZoom, so the click target cannot be the stroke.
        // ReactFlow draws a transparent path at this width; much past 24 and near-parallel
        // edges start stealing each other's clicks.
        interactionWidth: 24,
        ariaLabel: edgeAriaLabel(b, first, labelOf),
        data: { bundle: b },
        // Relation gets its own channel. Without arrowheads and dash patterns the direction
        // and the kind of relationship both have to be guessed from position.
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: active ? 'var(--accent)' : 'var(--text3)' },
        style: {
          stroke: active ? 'var(--accent)' : 'var(--text3)',
          strokeWidth: edgeActive ? 2.4 : active ? 2 : 1.3,
          strokeDasharray: b.rel === 'requires' ? '5 3' : b.rel === 'locks' ? '1 4' : undefined,
          opacity: hovered ? (active ? 1 : 0.1) : 0.75,
        },
      }
    })
  }, [bundles, hoveredEdgeId, labelOf])

  // ── Topology changed: lay out from scratch ──────────────────────────────────
  useEffect(() => {
    if (data.nodes.length === 0) { setNodes([]); setEdges([]); return }

    const positions = layoutNodes(data.nodes, data.edges, sizeOf)
    const rf = (pos: Map<string, Positioned>): Node[] => data.nodes.map((n) => {
      const p = pos.get(n.id) ?? { x: 0, y: 0 }
      const { w, h } = sizeOf(n)
      return {
        id: n.id,
        type: 'artefact',
        position: { x: p.x - w / 2, y: p.y - h / 2 },
        data: nodeDataFor(n) as unknown as Record<string, unknown>,
      }
    })

    const count = data.nodes.length
    const refit = () => setTimeout(
      () => fitWhenVisible({ padding: fitPadding(count), maxZoom: fitMaxZoom(count), duration: 300 }),
      50,
    )

    if (layoutMode === 'layered' || count > GRAPH_LARGE_THRESHOLD || data.edges.length === 0) {
      simRef.current?.stop()
      simRef.current = null
      setNodes(rf(positions))
      setEdges(buildEdges())
      refit()
      return
    }

    // Organic: dagre seeds the sim so it settles into a left-to-right flow rather than a blob.
    simRef.current?.stop()
    const simNodes: SimNode[] = data.nodes.map((n) => {
      const p = positions.get(n.id) ?? { x: 0, y: 0 }
      return { id: n.id, x: p.x, y: p.y, fx: null, fy: null }
    })
    simNodeIndex.current = new Map(simNodes.map((n) => [n.id, n]))
    const links = data.edges.map((e) => ({ source: e.from, target: e.to }))
    const spaceScale = Math.max(0.6, 1 - (count - 10) / 300)
    const depthOf = new Map(data.nodes.map((n) => [n.id, positions.get(n.id)?.x ?? 0]))

    const sim = forceSimulation<SimNode>(simNodes)
      .force('link', forceLink<SimNode, { source: string; target: string }>(links).id((n) => n.id).distance(150 * spaceScale).strength(0.5))
      .force('charge', forceManyBody().strength(-420).distanceMax(600))
      .force('collide', forceCollide(100 * spaceScale))
      // Hold the dagre x-ordering loosely, so the dependency direction survives the relax.
      .force('x', forceX<SimNode>((n) => depthOf.get(n.id) ?? 0).strength(0.2))
      .force('y', forceY<SimNode>(0).strength(0.1))
      .alphaDecay(0.03)
      .on('tick', () => {
        const dragging = draggingRef.current
        setNodes((prev) => prev.map((rfn) => {
          if (rfn.id === dragging) return rfn
          const sn = simNodeIndex.current.get(rfn.id)
          if (!sn) return rfn
          const d = rfn.data as unknown as DepNodeData
          return { ...rfn, position: { x: sn.x - d.size.w / 2, y: sn.y - d.size.h / 2 } }
        }))
      })
    sim.on('end', refit)
    simRef.current = sim

    setNodes(rf(new Map(simNodes.map((n) => [n.id, { id: n.id, x: n.x, y: n.y }]))))
    setEdges(buildEdges())
    refit()

    return () => { sim.stop() }
  // Positions depend on the topology only. Data refreshes are handled by the effect below, so
  // a refetch cannot reset the viewport.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topologySignature])

  // ── Data/emphasis changed: refresh node payloads in place ───────────────────
  useEffect(() => {
    const byId = new Map(data.nodes.map((n) => [n.id, n]))
    setNodes((prev) => prev.map((rfn) => {
      const n = byId.get(rfn.id)
      return n ? { ...rfn, data: nodeDataFor(n) as unknown as Record<string, unknown> } : rfn
    }))
    setEdges(buildEdges())
  }, [data.nodes, nodeDataFor, buildEdges, setNodes, setEdges])

  // ── Hover ───────────────────────────────────────────────────────────────────
  // Applied even while the sim is hot. The old version bailed out until the layout settled,
  // which made the graph feel dead for the first second or two.
  const applyHover = useCallback((id: string | null) => {
    hoveredRef.current = id
    const byId = new Map(data.nodes.map((n) => [n.id, n]))
    setNodes((prev) => prev.map((rfn) => {
      const n = byId.get(rfn.id)
      return n ? { ...rfn, data: nodeDataFor(n) as unknown as Record<string, unknown> } : rfn
    }))
    setEdges(buildEdges())
  }, [data.nodes, nodeDataFor, buildEdges, setNodes, setEdges])

  // ── Drag ────────────────────────────────────────────────────────────────────
  const pinSim = useCallback((id: string, x: number, y: number) => {
    const sn = simNodeIndex.current.get(id)
    if (sn) { sn.fx = x; sn.fy = y }
  }, [])

  const handleDragStart = useCallback((_e: React.MouseEvent, node: Node) => {
    draggingRef.current = node.id
    const d = node.data as unknown as DepNodeData
    pinSim(node.id, node.position.x + d.size.w / 2, node.position.y + d.size.h / 2)
    simRef.current?.alphaTarget(0.15).restart()
  }, [pinSim])

  const handleDrag = useCallback((_e: React.MouseEvent, node: Node) => {
    const d = node.data as unknown as DepNodeData
    pinSim(node.id, node.position.x + d.size.w / 2, node.position.y + d.size.h / 2)
  }, [pinSim])

  const handleDragStop = useCallback((_e: React.MouseEvent, node: Node) => {
    draggingRef.current = null
    const sn = simNodeIndex.current.get(node.id)
    if (sn) { sn.fx = null; sn.fy = null }
    simRef.current?.alphaTarget(0)
  }, [])

  // ── Click + context menu ────────────────────────────────────────────────────
  const handleNodeClick = useCallback((_e: React.MouseEvent, node: Node) => {
    const d = node.data as unknown as DepNodeData
    onNodeActivate?.(d.node)
  }, [onNodeActivate])

  const cancelEdgeTimers = useCallback(() => {
    if (edgeCloseTimer.current) clearTimeout(edgeCloseTimer.current)
    if (edgeOpenTimer.current) clearTimeout(edgeOpenTimer.current)
    edgeCloseTimer.current = null
    edgeOpenTimer.current = null
  }, [])

  /**
   * Hover opens the card, after a beat.
   *
   * The delay is what makes sweeping the canvas bearable: the cursor crosses edges it is not
   * asking about on the way to the one it is, and without it every crossing would throw a card
   * up. It is cancelled by leaving, so only a deliberate rest on a line opens anything.
   *
   * A PINNED card is not replaced by hovering the next edge - whoever pinned it asked for it to
   * stay until they closed it, and having it swapped out from under them while reaching for a
   * link inside is precisely the thing pinning exists to prevent.
   */
  const handleEdgeMouseEnter = useCallback((e: React.MouseEvent, edge: Edge) => {
    setHoveredEdgeId(edge.id)
    const bundle = (edge.data as { bundle?: EdgeBundle } | undefined)?.bundle
    if (!bundle) return
    cancelEdgeTimers()
    const at = { x: e.clientX, y: e.clientY }
    edgeOpenTimer.current = setTimeout(() => {
      setEdgeTarget((prev) => (prev?.pinned ? prev : { bundle, at, pinned: false }))
    }, EDGE_HOVER_DELAY_MS)
  }, [cancelEdgeTimers])

  const handleEdgeMouseLeave = useCallback(() => {
    setHoveredEdgeId(null)
    if (edgeOpenTimer.current) clearTimeout(edgeOpenTimer.current)
    edgeOpenTimer.current = null
    edgeCloseTimer.current = setTimeout(() => {
      setEdgeTarget((prev) => (prev?.pinned ? prev : null))
    }, EDGE_CLOSE_GRACE_MS)
  }, [])

  /** A click pins what hover is already showing, so the cursor can leave it. */
  const handleEdgeClick = useCallback((e: React.MouseEvent, edge: Edge) => {
    const bundle = (edge.data as { bundle?: EdgeBundle } | undefined)?.bundle
    if (!bundle) return
    cancelEdgeTimers()
    setEdgeTarget({ bundle, at: { x: e.clientX, y: e.clientY }, pinned: true })
  }, [cancelEdgeTimers])

  const closeEdgeCard = useCallback(() => {
    cancelEdgeTimers()
    setEdgeTarget(null)
  }, [cancelEdgeTimers])

  // A card left open while the component goes away would otherwise be closed by a timer that
  // fires into an unmounted tree.
  useEffect(() => cancelEdgeTimers, [cancelEdgeTimers])

  const handleContextMenu = useCallback((e: React.MouseEvent, node: Node) => {
    e.preventDefault()
    const d = node.data as unknown as DepNodeData
    setContextMenu({ x: e.clientX, y: e.clientY, node: d.node })
  }, [])

  const nodeCount = data.nodes.length

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={s.stateWrapper} style={{ height }}>
        <div className={s.stateInner}><span className="spinner" style={{ width: 10, height: 10 }} /> Loading graph…</div>
      </div>
    )
  }
  if (error) {
    return (
      <div style={{ height }}>
        <EmptyState icon="✕" title="Could not load the dependency graph." subtitle={error} />
      </div>
    )
  }

  return (
    <div ref={containerRef} className={s.canvas} style={{ height }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={depNodeTypes}
        onNodeClick={handleNodeClick}
        onNodeContextMenu={handleContextMenu}
        onNodeDragStart={handleDragStart}
        onNodeDrag={handleDrag}
        onNodeDragStop={handleDragStop}
        onNodeMouseEnter={(_e, n) => applyHover(n.id)}
        onNodeMouseLeave={() => applyHover(null)}
        onEdgeClick={handleEdgeClick}
        onEdgeMouseEnter={handleEdgeMouseEnter}
        onEdgeMouseLeave={handleEdgeMouseLeave}
        // Clicking the empty canvas dismisses a pinned card, the way clicking off any popover does.
        onPaneClick={closeEdgeCard}
        minZoom={0.08}
        style={{ background: 'var(--bg)' }}
        deleteKeyCode={null}
        nodesConnectable={false}
        edgesReconnectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="var(--border)" gap={24} />
        <Controls position="bottom-right" style={{ background: 'var(--card)', border: '1px solid var(--border)' }} />
        <Panel position="top-right"><GraphLegend /></Panel>
      </ReactFlow>

      {/* Empty state as an OVERLAY, so the canvas is never unmounted. With a filter rail and
          click-to-isolate, "no matches" is an ordinary state - remounting ReactFlow for it
          would throw away the viewport and re-run the fit every time. */}
      {nodeCount === 0 && (
        <div className={s.emptyOverlay}>
          <EmptyState
            icon="◈"
            title="Nothing matches the current view."
            subtitle="Clear a filter, or reset the view to see the whole graph."
          />
        </div>
      )}

      {nodeCount > 0 && (
        <div className={s.hint}>
          click a node to isolate · click a line for the relationship · right-click for actions
        </div>
      )}

      {contextMenu && createPortal(
        <GraphContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          node={contextMenu.node}
          currentOrg={currentOrg}
          navigable={navigable}
          blast={blastRadius?.get(contextMenu.node.id)}
          onDismiss={() => setContextMenu(null)}
          onIsolate={(n) => { onNodeActivate?.(n); setContextMenu(null) }}
          onHide={(n) => { onHideNode?.(n.id); setContextMenu(null) }}
          onLockFileHelp={(n) => { onLockFileHelp?.(n); setContextMenu(null) }}
          onDeprecate={(n) => { setDeprecateTarget(n); setContextMenu(null) }}
          onUndeprecate={(n) => { setUndeprecateTarget(n); setContextMenu(null) }}
          onBlock={(n) => { setBlockTarget(n); setContextMenu(null) }}
        />,
        document.body,
      )}

      {/* dependentCount is what makes these dialogs say "N modules depend on this" instead of
          staying silent. The graph is the one surface that knows the number exactly. */}
      {deprecateTarget?.latest_version && deprecateTarget.module_name && (
        <DeprecateModal
          namespace={deprecateTarget.namespace}
          name={deprecateTarget.module_name}
          provider={deprecateTarget.module_provider ?? ''}
          fixedVersion={deprecateTarget.latest_version}
          latestVersion={deprecateTarget.latest_version}
          dependentCount={blastRadius?.get(deprecateTarget.id)?.total ?? 0}
          onClose={() => setDeprecateTarget(null)}
          onDeprecated={() => { setDeprecateTarget(null); onMutated?.() }}
        />
      )}
      {undeprecateTarget?.latest_version && undeprecateTarget.module_name && (
        <UndeprecateModal
          namespace={undeprecateTarget.namespace}
          name={undeprecateTarget.module_name}
          provider={undeprecateTarget.module_provider ?? ''}
          version={undeprecateTarget.latest_version}
          onClose={() => setUndeprecateTarget(null)}
          onUndeprecated={() => { setUndeprecateTarget(null); onMutated?.() }}
        />
      )}
      {/* Owned here rather than by the page, so the module detail page's graph tab gets it
          with no wiring - it passes no callbacks at all. The card navigates for itself. */}
      {edgeTarget && (() => {
        const byId = new Map(data.nodes.map((n) => [n.id, n]))
        const fromNode = byId.get(edgeTarget.bundle.from)
        const toNode = byId.get(edgeTarget.bundle.to)
        // A filter can retire a node between the hover and this render; without both ends the
        // card has nothing honest to head itself with.
        if (!fromNode || !toNode) return null
        return (
          <GraphEdgeHoverCard
            bundle={edgeTarget.bundle}
            fromNode={fromNode}
            toNode={toNode}
            allEdges={data.edges}
            currentOrg={currentOrg}
            navigable={navigable}
            at={edgeTarget.at}
            pinned={edgeTarget.pinned}
            onMouseEnter={cancelEdgeTimers}
            onMouseLeave={handleEdgeMouseLeave}
            onClose={closeEdgeCard}
          />
        )
      })()}

      {blockTarget?.latest_version && blockTarget.module_name && (
        <BlockModal
          namespace={blockTarget.namespace}
          name={blockTarget.module_name}
          provider={blockTarget.module_provider ?? ''}
          version={blockTarget.latest_version}
          dependentCount={blastRadius?.get(blockTarget.id)?.total ?? 0}
          onClose={() => setBlockTarget(null)}
          onBlocked={() => { setBlockTarget(null); onMutated?.() }}
        />
      )}
    </div>
  )
}

// ── Node renderer ─────────────────────────────────────────────────────────────

interface DepNodeData {
  node: GraphNode
  size: KindGeometry
  highlighted: boolean
  searchDimmed: boolean
  isFocus: boolean
  isOrphan: boolean
  inCycle: boolean
  blast?: BlastRadius
  hoverState: 'none' | 'on' | 'off'
  currentOrg?: string
}

function ArtefactNode({ data }: { data: DepNodeData }) {
  const { node: n, size } = data
  const colour = statusColor(n)
  const { w, h } = size

  const opacity = data.hoverState === 'off' ? 0.12
    : data.searchDimmed ? 0.3
    : 1

  const ring = data.isFocus
    ? 'var(--blue)'
    : (data.highlighted || data.hoverState === 'on') ? colour
    : null

  const noLockFile = n.kind === 'state' && n.has_lock_file === false
  const sub = subLabel(n)

  return (
    <div
      className={s.node}
      style={{ width: w, height: h, opacity, cursor: 'pointer' }}
      title={`${KIND_LABEL[n.kind]} · ${n.label}\n${statusSummary(n)}`}
    >
      <svg
        className={s.silhouette}
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        // The shape is the border, so the stroke has to survive at any zoom.
        style={{ fill: n.external ? 'var(--bg2)' : 'var(--card)', stroke: colour, strokeWidth: 1.4 }}
      >
        {silhouette(n.kind, w, h)}
        {ring && (
          <g style={{ fill: 'none', stroke: ring, strokeWidth: 2.5, opacity: 0.85 }}>
            {silhouette(n.kind, w, h)}
          </g>
        )}
      </svg>

      {/* An orphan with no lock file gets a dashed overlay: the gap is the point, not a defect
          in the drawing. */}
      {noLockFile && (
        <svg className={s.silhouette} width={w} height={h} viewBox={`0 0 ${w} ${h}`}
          style={{ fill: 'none', stroke: 'var(--text3)', strokeWidth: 1.2, strokeDasharray: '4 4' }}>
          {silhouette(n.kind, w, h)}
        </svg>
      )}

      <Handle type="target" position={Position.Left} className={s.handle} style={{ background: colour }} />

      <div className={s.nodeBody} style={labelInset(n.kind)}>
        <div className={s.nodeRow1}>
          <span className={s.kindGlyph} style={{ color: colour }}>{KIND_GLYPH[n.kind](11)}</span>
          <span className={s.nodeName}>{n.label}</span>
          <span className={s.nodeIcons}>
            {data.inCycle && <span className={s.iconAmber} title="On a dependency cycle">↻</span>}
            {n.blocked && <span className={s.iconRed} title={n.blocked_reason ?? 'blocked'}>✗</span>}
            {!n.blocked && (n.deprecated || n.outdated) && <span className={s.iconAmber}>⚠</span>}
            {!n.blocked && n.policy_failed && <span className={s.iconAccent} title="Fails a policy">✗</span>}
          </span>
        </div>
        <div className={s.nodeRow2}>
          <span className={s.versionText}>{sub}</span>
          {n.critical_count > 0 && <span className={s.findingCritical}>●{n.critical_count}</span>}
          {n.high_count > 0 && <span className={s.findingHigh}>●{n.high_count}</span>}
          {(data.blast?.total ?? 0) > 0 && (
            <span className={s.blastCount} title={blastTitle(data.blast!)}>
              ↑{data.blast!.total}
            </span>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Right} className={s.handle} style={{ background: colour }} />
    </div>
  )
}

/**
 * What a screen reader announces for a focused edge. Worth the few lines: an edge that
 * announces only "edge" is a tab stop with no content, and the pinned version is the whole
 * reason the edge is reachable at all.
 */
function edgeAriaLabel(b: EdgeBundle, first: GraphEdge, labelOf: (id: string) => string): string {
  const verb = b.rel === 'locks' ? 'locks' : b.rel === 'requires' ? 'requires' : 'depends on'
  const from = labelOf(b.from)
  const to = labelOf(b.to)
  const detail = first.locked_version
    ? `, pinned ${first.locked_version}`
    : first.constraint
      ? `, ${first.constraint}`
      : ''
  const extra = b.edges.length > 1 ? ` and ${b.edges.length - 1} more` : ''
  return `${from} ${verb} ${to}${detail}${extra}`
}

function subLabel(n: GraphNode): string {
  if (n.kind === 'state') {
    if (n.has_lock_file === false) return 'no lock file'
    return n.serial != null ? `serial ${n.serial}` : 'workspace'
  }
  if (n.kind === 'provider') {
    if (n.outdated) return 'outdated pin'
    return n.discovered ? 'discovered' : 'added'
  }
  if (n.external) return 'external'
  if (n.version_count === 0) return 'no published version'
  return `v${n.latest_version} · ${n.version_count} ver`
}

function blastTitle(b: BlastRadius): string {
  const parts: string[] = []
  if (b.byKind.state > 0) parts.push(`${b.byKind.state} state file${b.byKind.state === 1 ? '' : 's'}`)
  if (b.byKind.module > 0) parts.push(`${b.byKind.module} module${b.byKind.module === 1 ? '' : 's'}`)
  if (b.byKind.provider > 0) parts.push(`${b.byKind.provider} provider${b.byKind.provider === 1 ? '' : 's'}`)
  return `${b.total} depend on this: ${parts.join(', ')}`
}

const depNodeTypes: NodeTypes = { artefact: ArtefactNode as unknown as NodeTypes[string] }

// ── Legend ────────────────────────────────────────────────────────────────────

/**
 * Two columns, because there are two channels. One legend replaces the two the old pair of
 * graphs carried, which disagreed about what --accent and --green meant.
 */
const LEGEND_KEY = 'tm-graph-legend'

function GraphLegend() {
  const [expanded, setExpanded] = useState(() => {
    // Browser storage can throw in a private window or with site data blocked.
    try { return localStorage.getItem(LEGEND_KEY) !== 'collapsed' } catch { return true }
  })
  const toggle = () => setExpanded((v) => {
    try { localStorage.setItem(LEGEND_KEY, v ? 'collapsed' : 'expanded') } catch { /* not worth surfacing */ }
    return !v
  })
  return (
    <div className={s.legend}>
      <button className={s.legendToggle} onClick={toggle}>
        {expanded ? '▾ legend' : '▸ legend'}
      </button>
      {expanded && (
        <div className={s.legendCols}>
          <div className={s.legendBody}>
            <div className={s.legendHead}>shape</div>
            {(['state', 'module', 'provider'] as GraphKind[]).map((k) => (
              <div key={k} className={s.legendItem}>
                <span className={s.legendGlyph}>{KIND_GLYPH[k](12)}</span>
                <span className={s.legendLabel}>{KIND_LABEL[k]}</span>
              </div>
            ))}
          </div>
          <div className={s.legendBody}>
            <div className={s.legendHead}>colour</div>
            {[
              { c: 'var(--green)', l: 'healthy' },
              { c: 'var(--amber)', l: 'deprecated / outdated' },
              { c: 'var(--red)', l: 'blocked / critical' },
              { c: 'var(--accent)', l: 'policy failed' },
              { c: 'var(--border)', l: 'external' },
            ].map(({ c, l }) => (
              <div key={l} className={s.legendItem}>
                <div className={s.legendDot} style={{ background: c }} />
                <span className={s.legendLabel}>{l}</span>
              </div>
            ))}
          </div>
          <div className={s.legendBody}>
            <div className={s.legendHead}>edges</div>
            <div className={s.legendItem}><span className={s.legendLineSolid} /><span className={s.legendLabel}>depends on</span></div>
            <div className={s.legendItem}><span className={s.legendLineDashed} /><span className={s.legendLabel}>requires</span></div>
            <div className={s.legendItem}><span className={s.legendLineDotted} /><span className={s.legendLabel}>locks</span></div>
            <div className={s.legendFoot}>click one for versions</div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Export ────────────────────────────────────────────────────────────────────

export function DepGraph(props: DepGraphProps) {
  return (
    <ReactFlowProvider>
      <DepGraphInner {...props} />
    </ReactFlowProvider>
  )
}
